import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationStart, Router } from '@angular/router';
import { environment } from 'environments/environment';
import { EMPTY, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { AppUpdateDialogComponent } from './app-update-dialog.component';

interface VersionManifest {
    version: string;
}

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
    private readonly http = inject(HttpClient);
    private readonly dialog = inject(MatDialog);
    private readonly router = inject(Router);

    private readonly stop$ = new Subject<void>();
    private started = false;
    private dialogShown = false;

    startMonitoring(): void {
        if (this.started) {
            return;
        }
        this.started = true;

        this.router.events
            .pipe(
                filter(
                    (event): event is NavigationStart =>
                        event instanceof NavigationStart && !this.dialogShown
                ),
                takeUntil(this.stop$),
                switchMap(() =>
                    this.getLatestVersion().pipe(catchError(() => EMPTY))
                )
            )
            .subscribe((latestVersion) => {
                if (!latestVersion) {
                    return;
                }

                if (
                    latestVersion !== environment.version &&
                    !this.dialogShown
                ) {
                    this.dialogShown = true;
                    this.openUpdateDialog(latestVersion);
                }
            });
    }

    stopMonitoring(): void {
        this.stop$.next();
        this.stop$.complete();
    }

    private getLatestVersion() {
        const headers = new HttpHeaders({
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
            Expires: '0',
        });

        return this.http
            .get<VersionManifest>(`/version.json?t=${Date.now()}`, { headers })
            .pipe(map((manifest) => manifest?.version?.trim() ?? ''));
    }

    private openUpdateDialog(latestVersion: string): void {
        this.dialog.open(AppUpdateDialogComponent, {
            disableClose: true,
            hasBackdrop: true,
            width: '520px',
            maxWidth: '95vw',
            data: {
                currentVersion: environment.version,
                latestVersion,
            },
        });
    }
}
